import { invalidateAnnotationQueries } from "@/lib/data/react-query/annotation-query-utils";
import {
  createMatterTaskCommentV1,
  deleteMatterTaskCommentV1,
  updateMatterTaskCommentV1,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static";
import { type APIError } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

const invalidateMatterTasks = (queryClient: QueryClient, matterId?: string) => {
  if (!matterId) {
    return Promise.resolve();
  }

  return queryClient.invalidateQueries({
    queryKey: [queryKeys.matters, matterId, "tasks"],
  });
};

const invalidateTaskCommentQueries = async (
  queryClient: QueryClient,
  variables: { matterId?: string; massTortId?: string },
) => {
  await invalidateMatterTasks(queryClient, variables.matterId);
  await invalidateAnnotationQueries(queryClient, variables);
};

export const useCreateMatterTaskComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterTaskCommentV1>[0]) =>
      createMatterTaskCommentV1(data),
    onMutate: (data) => {
      if (data.massTortId) {
        return;
      }
      return undefined;
    },
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error creating matter task comment");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateTaskCommentQueries(queryClient, variables);
    },
    onSuccess: () => {
      toast.success("Matter task comment created successfully");
    },
  });
};

export const useUpdateMatterTaskComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterTaskCommentV1>[0]) =>
      updateMatterTaskCommentV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating matter task comment");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateTaskCommentQueries(queryClient, variables);
    },
    onSuccess: () => {
      toast.success("Matter task comment updated successfully");
    },
  });
};

export const useDeleteMatterTaskComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterTaskCommentV1>[0]) =>
      deleteMatterTaskCommentV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error deleting matter task comment");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateTaskCommentQueries(queryClient, variables);
    },
    onSuccess: () => {
      toast.success("Matter task comment deleted successfully");
    },
  });
};
